/* hs-eslint ignored failing-rules */
/* eslint-disable hubspot-dev/no-unsafe-i18n-at-module-eval */

'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["user"];
import userInfo from 'hub-http/userInfo';
import PortalIdParser from 'PortalIdParser';
import Raven from 'raven-js';
import { currentProject } from 'hubspot.bender';
import enviro from 'enviro';
import { Provider } from 'react-redux';
// TODO: When all CMS editors are on Quartz, update this library to be quartz-exclusive and remove these providers
// @bend-expect quartz-no-already-provided-providers
import { Router } from 'quartz-routing/react-router';
import { LOCATION_CHANGE } from './redux/actions/actionTypes';

// Import extra I18n data
import 'I18n/init/data/currency';
import 'I18n/init/data/baseLocales';
import 'I18n/init/data/publicLocales';
import 'I18n/init/data/allTimezones';
import { hydrateUserInfo } from 'ui-asset-management-lib';
import { getInitialOptinView } from 'ui-addon-opt/state/UIOptAvailability';

// @bend-expect quartz-no-already-provided-providers
import { RhumbProvider } from 'react-rhumb';
import historyAdapter from 'react-rhumb/historyAdapterV4';
import { DataFetchingClientProvider, DataFetchingClient } from 'data-fetching-client';
import { ApolloProvider } from '@apollo/client';
import { createApolloClient } from 'hubspot-apollo-client';
import { CampaignsProvider } from 'campaigns-lib/campaignsPlatform';
// @bend-expect quartz-no-already-provided-providers
import UIApplication from 'UIComponents/app/UIApplication';
import { renderRoot } from 'quartz-core/client';
import ContentRoutes from 'ContentUtils/Routes';
import createHistory from 'ContentEditorUI/redux/createHistory';
import { configureStore } from 'ContentEditorUI/redux/configureStore';
import SyncConfigManager from 'ContentEditorUI/redux/inpageSync/SyncConfigManager';
import { startApp } from 'ContentEditorUI/redux/actions/appActions';
import syncAndCacheUserSettingsMiddleware from 'ContentEditorUI/redux/middleware/syncAndCacheUserSettings';
import contentSchemaValidationMiddleware from 'ContentEditorUI/redux/middleware/contentSchemaValidationMiddleware';
import InitializeScalableEditorActionsAndEventsContext from 'ContentEditorUI/containers/InitializeScalableEditorActionsAndEventsContext';
import { identifyUserForFullStory } from 'ContentEditorUI/utils/identifyUser';
import { validateEditorConfig } from 'ContentEditorUI/utils/validateEditorConfig';

// Legacy imports
import EventBus from 'ContentEditorUI/coffee/lib/eventBus';
import { trackQueuedLifecycleTimestamps } from 'ContentEditorUI/utils/lifecycleData';
import AsyncComponentContext from 'ContentEditorUI/context/AsyncComponentContext';
import { getUserSuperstoreInstance, getPerContentSuperstoreInstance } from 'ContentEditorUI/lib/superstore';
import { prepareIntitialSettingsStateFromSuperstores } from 'ContentEditorUI/lib/userAndLocalCacheSettings/helpers';
import createStandardI18nProvider from 'I18n/init/providers/createStandardI18nProvider';
import registerAllData from 'I18n/init/register/sync/registerAllData';
import I18n from 'I18n';
import { handleInitialRequests } from 'ContentEditorUI/UniversalRequestUtils';
import EditorConfigSingleton from './EditorConfigSingleton';
import logSeleniumEvent from 'ContentEditorUI/utils/logSeleniumEvent';
import { enableMapSet } from 'immer';
import { setInitialStateForDiffedReducers, addInitialStateLookupForDiffedReducer } from 'ContentEditorUI/redux/reduxReducerDiffer';
import { getIsFocusModeOn } from './redux/selectors/localCachedSettingsSelectors';
import { showFocusModeOnOverlay } from './lib/layoutDom';
import { getHasPermissionsToSave } from 'ContentEditorUI/utils/editorConfigSingletonUtils';
import ReducerDifferManager from './redux/ReducerDifferManager';
import MaybeCopilotWrapper from './MaybeCopilotWrapper.tsx';
import { jsx as _jsx } from "react/jsx-runtime";
enableMapSet();
const defaultMakeInitialState = () => {
  return {};
};

// Yell loudly when translations are missing
I18n.sendSentryOnMissingTranslations = true;
if (!window.hubspot) {
  window.hubspot = {};
}
if (!window.hubspot.ContentEditorUI) {
  window.hubspot.ContentEditorUI = {};
}
const client = new DataFetchingClient();
const apolloClient = createApolloClient();
const DM_CODE_ASSISTANT_OPT_IN = 'Design Manager Code Assistant';
const fetchOptIns = () => {
  const optIns = {
    [DM_CODE_ASSISTANT_OPT_IN]: getInitialOptinView(DM_CODE_ASSISTANT_OPT_IN)
  };

  // Wait for all the promises in the optIns object
  const keys = Object.keys(optIns);
  const promises = keys.map(key => optIns[key]);
  return Promise.all(promises).then(resolvedValues => {
    const resolvedOptIns = {};
    keys.forEach((key, index) => {
      resolvedOptIns[key] = resolvedValues[index];
    });
    return resolvedOptIns;
  }).catch(() => {
    // Default to no optIns if we fail to fetch them. We don't log this error because it often occurs when a user loads the page unauthenticated.
    return {};
  });
};
const initAuth = () => {
  return userInfo().then(hydrateUserInfo).then(({
    portal,
    gates,
    user,
    userObjectTimezone
  }) => {
    Raven.setUserContext({
      id: user.user_id,
      email: user.email,
      locale: user.locale
    });
    identifyUserForFullStory(user, currentProject);

    // Initialize content routes
    ContentRoutes.init(gates);
    return {
      portal,
      gates,
      user,
      userObjectTimezone
    };
  }).catch(error => {
    throw error;
  });
};
const startI18n = (auth, initialSettingsFromSuperstore, editorConfig) => {
  const {
    user,
    portal,
    userObjectTimezone
  } = auth;
  const {
    locale,
    lang_enabled: langEnabled
  } = user;
  const {
    timezone
  } = portal;
  const I18nProvider = createStandardI18nProvider();
  const langAvailable = Promise.all([I18nProvider.register(editorConfig.infra.lang), registerAllData(I18nProvider)]);
  I18nProvider.setLocale({
    locale,
    langEnabled,
    timezone,
    userObjectTimezone
  });
  return Promise.all([auth, initialSettingsFromSuperstore, langAvailable]);
};
const modifyScopesForSelenium = scopes => {
  let scopesToAdd = [];
  let scopesToRemove = [];
  try {
    scopesToAdd = window.localStorage.getItem('SELENIUM_SCOPES_TO_ADD') || '';
    scopesToRemove = window.localStorage.getItem('SELENIUM_SCOPES_TO_REMOVE') || '';
  } catch (e) {
    console.warn('Couldnt load up scope modifications');
  }
  scopesToAdd.split(',').forEach(scopeToAdd => {
    scopes.push(scopeToAdd);
  });
  scopesToRemove.split(',').forEach(scopeToRemove => {
    const scopeIndex = scopes.findIndex(scope => scope === scopeToRemove);
    if (scopeIndex > -1) {
      scopes.splice(scopeIndex, 1);
    }
  });
  return scopes;
};
function makeInitialIframeState() {
  if (!EditorConfigSingleton.getHasIframe()) {
    return {};
  }
  return {
    iframes: {
      previewDomain: window.hsPreviewDomain,
      previewIframeUrl: window.hsPreviewIframeUrl,
      sidebarIframeUrl: window.hsSidebarIframeUrl
    }
  };
}
export const startRedux = ({
  user,
  gates,
  portal,
  eventBus,
  history,
  contentId,
  initialSettingsFromSuperstore,
  editorConfig
}) => {
  const {
    api,
    redux
  } = editorConfig;

  // Add ability for selenium test to modify scopes
  if (window.SELENIUM_IS_RUNNING) {
    user.scopes = modifyScopesForSelenium(user.scopes);
  }
  const makeInitialState = editorConfig.redux.makeInitialState || defaultMakeInitialState;
  addInitialStateLookupForDiffedReducer('locallyCachedSettings', initialSettingsFromSuperstore.toJS());
  const initialState = Object.assign({
    auth: {
      user,
      gates,
      portal
    },
    locallyCachedSettings: initialSettingsFromSuperstore,
    extraInitialState: {
      baseApiUrl: api.API_BASE_URL,
      editorConfig,
      appKey: editorConfig.appKey
    }
  }, makeInitialIframeState(), makeInitialState(gates));
  const customMiddlewaresFromConfig = redux.customMiddlewares || [];
  const customMiddlewares = [syncAndCacheUserSettingsMiddleware({
    contentId
  }), redux.checkAppReadyMiddleware, ...customMiddlewaresFromConfig];
  if (enviro.isQa()) {
    customMiddlewares.push(contentSchemaValidationMiddleware());
  }
  setInitialStateForDiffedReducers();
  // It's possible that ReducerDifferManager has already been called before we
  // set the gates, which can lead to cached false negatives due to checking
  // against an empty list then caching the false values, so we explicitly clear
  // the cache first before setting the gates.
  ReducerDifferManager.clear();
  ReducerDifferManager.setGates(gates);
  const store = configureStore(redux.reducers.rootReducer, initialState, eventBus, history, SyncConfigManager.getConfig(), undefined,
  // stateSelectorForErrorHandling
  customMiddlewares);
  store.dispatch(startApp());

  // Temporary... yeah, right :P
  window.hubspot.ContentEditorUI.store = store;
  return store;
};

// When migrating to ts - action is typed in EmailEditorUI/redux/actions/types/layoutActions
const createLocationChangedAction = store => {
  return location => {
    store.dispatch({
      type: LOCATION_CHANGE,
      payload: location
    });
  };
};
const startRouter = (store, history, contentId, editorConfig) => {
  var _window$hubspot, _ref;
  const {
    infra: {
      rhumbConfig
    },
    routes,
    components: {
      asyncComponents,
      RootTreeWrapper
    }
  } = editorConfig;
  const CopilotCollaborationContextProvider = EditorConfigSingleton.getComponents().CopilotCollaborationContextProvider;

  // Default appName to current project if editorConfig.appName is not provided
  const benderCurrentProject = (_window$hubspot = window.hubspot) === null || _window$hubspot === void 0 || (_window$hubspot = _window$hubspot.bender) === null || _window$hubspot === void 0 ? void 0 : _window$hubspot.currentProject;
  const editorConfigAppName = editorConfig.appName;
  const nameForUIApplication = (_ref = editorConfigAppName !== null && editorConfigAppName !== void 0 ? editorConfigAppName : benderCurrentProject) !== null && _ref !== void 0 ? _ref : 'ContentEditorUI-unknownEditor';
  renderRoot( /*#__PURE__*/_jsx(RhumbProvider, {
    config: rhumbConfig,
    history: historyAdapter(history),
    children: /*#__PURE__*/_jsx(Provider, {
      store: store,
      children: /*#__PURE__*/_jsx(AsyncComponentContext.Provider, {
        value: asyncComponents,
        children: /*#__PURE__*/_jsx(ApolloProvider, {
          client: apolloClient,
          children: /*#__PURE__*/_jsx(DataFetchingClientProvider, {
            client: client,
            children: /*#__PURE__*/_jsx(InitializeScalableEditorActionsAndEventsContext, {
              children: /*#__PURE__*/_jsx(MaybeCopilotWrapper, {
                CopilotCollaborationContextProviderComponent: CopilotCollaborationContextProvider,
                children: /*#__PURE__*/_jsx(CampaignsProvider, {
                  children: /*#__PURE__*/_jsx(UIApplication, {
                    name: nameForUIApplication,
                    children: /*#__PURE__*/_jsx(RootTreeWrapper, {
                      contentId: contentId,
                      children: /*#__PURE__*/_jsx(Router, {
                        history: history,
                        children: routes()
                      })
                    })
                  })
                })
              })
            })
          })
        })
      })
    })
  }));
};
export const start = editorConfig => {
  if (enviro.isQa()) {
    validateEditorConfig(editorConfig);
  }
  const {
    setupDevHelpers
  } = editorConfig;
  const portalId = PortalIdParser.get();
  const {
    contentId
  } = window.hubspot.parseEditorUrl();
  logSeleniumEvent('appStarting');

  // Setting up the legacy-ish event bus early, because it is
  // connected to the Redux store via a custom middleware
  const eventBus = EventBus;
  window.hubspot.ContentEditorUI.eventBus = eventBus;
  const optInPromises = fetchOptIns();
  const authPromise = initAuth();
  Promise.all([authPromise, prepareIntitialSettingsStateFromSuperstores(getUserSuperstoreInstance(), getPerContentSuperstoreInstance(contentId)), optInPromises]).then(([_ref2, initialSettingsFromSuperstore, optIns]) => {
    let {
        user
      } = _ref2,
      auth = _objectWithoutPropertiesLoose(_ref2, _excluded);
    return startI18n(Object.assign({}, auth, {
      user: Object.assign({}, user, {
        optIns
      })
    }), initialSettingsFromSuperstore, editorConfig);
  }).then(([{
    user,
    gates,
    portal
  }, initialSettingsFromSuperstore]) => {
    const rootUrlWithPortalId = editorConfig.infra.rootUrl.replace('{portalId}', portalId);
    EditorConfigSingleton.setIsOnScalableEditor(true);
    EditorConfigSingleton.set(editorConfig);
    EditorConfigSingleton.setGates(gates);
    const history = createHistory(rootUrlWithPortalId);
    const store = startRedux({
      user,
      gates,
      portal,
      eventBus,
      history,
      contentId,
      initialSettingsFromSuperstore,
      editorConfig
    });

    // If the user's focus mode setting is on, we want to show an alternative overlay
    // that masks the full inpage preview while we setup the focus mode UI
    if (editorConfig.features['focusMode'] && getIsFocusModeOn(store.getState())) {
      showFocusModeOnOverlay();
    }
    const locationChangedListener = createLocationChangedAction(store);
    history.listen(locationChangedListener);
    // react-router-redux dispatches LOCATION_CHANGE on load which we've relied on
    // so we're doing the same here
    if (history.location) {
      locationChangedListener(history.location);
    }
    handleInitialRequests(store, editorConfig.api.initialRequests);
    startRouter(store, history, contentId, editorConfig);

    // Some internal developer help/pointers printed in the log (only on QA and local development)
    if (setupDevHelpers) {
      setupDevHelpers({
        eventBus
      });
    }

    // Make sure we track any of the lifecycle data that was queued up by the early requestor
    trackQueuedLifecycleTimestamps();
    const hasPermissionsToSave = getHasPermissionsToSave(store.getState());
    EditorConfigSingleton.setHasPermissionsToSave(hasPermissionsToSave);
  }).catch(error => {
    if (error.message !== 'Aborting: redirection in progress') {
      throw error;
    }
  });
};